import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const ReviewEdit = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();

  // 상태 관리
  const [rating, setRating] = useState('');
  const [content, setContent] = useState('');
  const [existingImage, setExistingImage] = useState(null); // 기존 이미지 경로
  const [previewUrl, setPreviewUrl] = useState(null);       // 새 이미지 미리보기
  const [selectedFile, setSelectedFile] = useState(null);   // 실제 업로드할 파일
  const [loading, setLoading] = useState(true);

  // 1. 기존 데이터 불러오기 (GET)
  useEffect(() => {
    const fetchReview = async () => {
      try {
        const res = await axios.get(`/api/review/edit/${reviewId}`);
        const data = res.data.review;
        if (data) {
          setRating(data.rating);
          setContent(data.content);
          setExistingImage(data.image_path);
        }
      } catch (err) {
        console.error("데이터 로드 실패:", err);
        alert("리뷰 정보를 불러올 수 없습니다.");
        navigate('/reviews');
      } finally {
        setLoading(false);
      }
    };
    fetchReview();
  }, [reviewId, navigate]);

  // 2. 파일 선택 및 미리보기 로직
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setPreviewUrl(null);
      setSelectedFile(null);
      return;
    }

    // 브라우저 미리보기 URL 생성
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setSelectedFile(file);
  };

  // 3. 이미지 리사이징 (기존 Canvas 활용 로직 이식)
  const resizeImage = (file, maxWidth) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;
          if (width > maxWidth) {
            const scale = maxWidth / width;
            width = maxWidth;
            height *= scale;
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // 4. 수정 제출 (POST)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData();
    formData.append('rating', rating);
    formData.append('content', content);

    if (selectedFile) {
      // 이미지 리사이징 후 추가
      const resizedBlob = await resizeImage(selectedFile, 1200);
      formData.append('image', resizedBlob, selectedFile.name);
    }

    try {
      const res = await axios.post(`/api/review/edit/${reviewId}`, formData);
      if (res.data.success) {
        alert("리뷰가 성공적으로 수정되었습니다.");
        navigate('/reviews'); // 수정 완료 후 목록으로 이동
      }
    } catch (err) {
      console.error("수정 실패:", err);
      alert("업데이트 실패: " + (err.response?.data?.message || "서버 오류"));
    }
  };

  if (loading) return <div className="container mt-5 text-center">로딩 중...</div>;

  return (
    <div className="container my-4" style={styles.container}>
      <h2 className="mb-4" style={styles.title}>리뷰 수정</h2>
      <form onSubmit={handleSubmit} noValidate>
        {/* 별점 선택 */}
        <div className="mb-3">
          <label htmlFor="rating" className="form-label">별점</label>
          <select 
            id="rating"
            className="form-select" 
            value={rating} 
            onChange={(e) => setRating(e.target.value)}
            required
          >
            <option value="">선택하세요</option>
            {[5, 4, 3, 2, 1].map(num => (
              <option key={num} value={num}>{num}점</option>
            ))}
          </select>
        </div>

        {/* 리뷰 내용 */}
        <div className="mb-3">
          <label htmlFor="content" className="form-label">리뷰 내용</label>
          <textarea 
            id="content"
            className="form-control" 
            rows="5" 
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
          />
        </div>

        {/* 이미지 업로드 및 미리보기 */}
        <div className="mb-3">
          <label className="form-label">이미지 (선택)</label><br />
          
          {/* 우선순위: 
            1. 새로 선택한 파일의 미리보기(previewUrl) 
            2. 기존에 저장되어 있던 이미지(existingImage) 
          */}
          {(previewUrl || existingImage) && (
            <div className="mb-2">
              <img 
                src={previewUrl || (existingImage?.startsWith('http') ? existingImage : `/static/${existingImage}`)} 
                alt="리뷰 이미지" 
                style={styles.previewImage}
              />
            </div>
          )}
          
          <input 
            type="file" 
            className="form-control mt-2" 
            accept="image/*" 
            onChange={handleFileChange}
          />
        </div>

        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary">수정 완료</button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>취소</button>
        </div>
      </form>
    </div>
  );
};

// 스타일 정의 (Reviews.jsx와 일관성 유지)
const styles = {
  container: { maxWidth: '600px', margin: '20px auto 40px', padding: '0 15px' },
  title: { fontSize: '1.5rem', fontWeight: 'bold' },
  previewImage: { maxWidth: '200px', borderRadius: '6px', border: '1px solid #ddd' }
};

export default ReviewEdit;